import { useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/cms/ui/alert-dialog";

interface ExitConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaveAndExit: () => void;
  onDiscardAndExit: () => void;
  title?: string;
  description?: string;
  saveButtonText?: string;
  discardButtonText?: string;
  saving?: boolean;
}

export function ExitConfirmationDialog({
  open,
  onOpenChange,
  onSaveAndExit,
  onDiscardAndExit,
  title = "Unsaved Changes",
  description = "You have unsaved changes. Would you like to save your changes before leaving, or discard them?",
  saveButtonText = "Save Changes",
  discardButtonText = "Discard Changes",
  saving
}: ExitConfirmationDialogProps) {
  // Handle Enter key press to trigger save changes
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Blur any focused element to prevent blue border
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        
        if (!saving) onSaveAndExit();
      }
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, onSaveAndExit, saving]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onDiscardAndExit} disabled={saving}>
            {discardButtonText}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={saving}
            onClick={(event) => {
              // A page that passes `saving` closes the dialog itself, so a failed save keeps the user on the page
              if (saving !== undefined) event.preventDefault();
              onSaveAndExit();
            }}
          >
            {saving ? 'Saving...' : saveButtonText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
} 